import { defineStore } from 'pinia'
import gql from 'graphql-tag'
import apolloClient from "../plugins/apollo"
import { logError } from '@/utils/logger'

// GraphQL запрос - вынесен в константу для переиспользования
const CART_QUERY = gql`
    query {
        cart {
            items(first: 100) {
                totalQuantity
                hasUnavailableForCheckoutProducts
                totalPrice
                edges {
                    node {
                        quantity
                        product {
                            name
                            id
                            slug
                            quantityMultiplicity
                            stockAvailabilityStatus
                            mainImage {
                                url
                            }
                            category {
                                slug
                            }
                            price
                            stockAvailabilityStatus
                        }
                        id
                    }
                }
            }
        }
    }
`

const UPDATE_CART_ITEM_MUTATION = gql`
    mutation UpdateCartItemQuantity($productId: ID!, $quantity: Int!) {
        updateCartItemQuantity(productId: $productId, quantity: $quantity) {
            __typename
        }
    }
`

const STORAGE_KEY = 'kron_cart_state';

// Функция для сохранения состояния в localStorage
const saveToStorage = (state) => {
    try {
        const stateToSave = {
            cart: state.cart,
            cartCount: state.cartCount,
            cartPrice: state.cartPrice,
            cartItems: state.cartItems,
            timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
        logError('Failed to save cart to localStorage', error)
    }
};

// Функция для загрузки состояния из localStorage
const loadFromStorage = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            // Проверяем, что данные не старше 7 дней
            const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 дней
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < maxAge) {
                return parsed;
            }
        }
    } catch (error) {
        logError('Failed to load cart from localStorage', error)
    }
    return null;
};

export const useCart = defineStore('cart', {
    state: () => {
        // Пытаемся загрузить из localStorage при инициализации
        const saved = loadFromStorage();
        
        return {
            cart: saved?.cart || {}, // Изменено: объект вместо массива для быстрого поиска
            cartCount: saved?.cartCount || 0,
            cartPrice: saved?.cartPrice || 0,
            cartItems: saved?.cartItems || [], // Массив items для отображения
            isLoading: false
        };
    },

    getters: {
        // Проверка наличия товара в корзине
        hasProduct: (state) => (productId) => {
            return state.cart[productId] > 0;
        },
        // Количество конкретного товара
        productQuantity: (state) => (productId) => {
            return state.cart[productId] || 0;
        }
    },

    actions: {
        // Обновляет количество товара в объекте cart (малая, сфокусированная функция)
        _updateCartItem(productId, quantity) {
            if (quantity > 0) {
                this.cart[productId] = quantity;
            } else {
                delete this.cart[productId];
            }
        },
        
        // Обновляет счетчик корзины (малая, сфокусированная функция)
        _updateCartCount(quantityDiff) {
            this.cartCount = Math.max(0, (this.cartCount || 0) + quantityDiff);
        },
        
        // Обновляет цену корзины (малая, сфокусированная функция)
        _updateCartPrice(priceDiff) {
            this.cartPrice = Math.max(0, (this.cartPrice || 0) + priceDiff);
        },
        
        // Обновляет количество в cartItems (малая, сфокусированная функция)
        _updateCartItemQuantity(productId, quantity) {
            const cartItem = this.cartItems.find(item => item.node?.product?.id === productId);
            if (cartItem && cartItem.node) {
                cartItem.node.quantity = quantity;
            }
        },
        
        // Оптимистичное обновление количества товара (композиция малых функций)
        updateProductQuantity(productId, quantity, saveToLocalStorage = false) {
            if (!productId) return;
            
            const oldQuantity = this.cart[productId] || 0;
            const quantityDiff = quantity - oldQuantity;
            
            // Если количество не изменилось и нужно только сохранить - просто сохраняем
            if (quantityDiff === 0 && saveToLocalStorage) {
                saveToStorage(this.$state);
                return;
            }
            
            // Обновляем количество товара
            this._updateCartItem(productId, quantity);
            
            // Обновляем счетчик корзины
            this._updateCartCount(quantityDiff);
            
            // Обновляем цену (оптимистично, если есть данные в cartItems)
            const cartItem = this.cartItems.find(item => item.node?.product?.id === productId);
            if (cartItem && cartItem.node?.product?.price) {
                const priceDiff = cartItem.node.product.price * quantityDiff;
                this._updateCartPrice(priceDiff);
            }
            
            // Обновляем cartItems локально
            this._updateCartItemQuantity(productId, quantity);
            
            // Сохраняем в localStorage если нужно
            if (saveToLocalStorage) {
                saveToStorage(this.$state);
            }
        },
        
        async refreshCart() {
            this.isLoading = true;
            
            try {
                const { data } = await apolloClient.query({
                    query: CART_QUERY,
                    fetchPolicy: "no-cache"
                });

                const cartData = data.cart;
                this.cartItems = Array.isArray(cartData.items?.edges) ? cartData.items.edges : [];

                await this.normalizeCartMultiplicities();
                this.recalculateCartState();
                this.removeUnavailableFromTotals();
                saveToStorage(this.$state);

                this.isLoading = false;
            } catch (error) {
                logError('Failed to refresh cart', error)
                this.cart = {};
                this.cartCount = 0;
                this.cartPrice = 0;
                this.cartItems = [];
                
                // Сохраняем пустое состояние
                saveToStorage(this.$state);
                
                this.isLoading = false;
                // Можно добавить уведомление пользователю
            }
        },
        
        // Метод для очистки сохраненной корзины
        clearSavedCart() {
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch (error) {
                logError('Failed to clear saved cart', error)
            }
        },

        async normalizeCartMultiplicities() {
            if (!Array.isArray(this.cartItems) || !this.cartItems.length) {
                return;
            }

            for (const edge of this.cartItems) {
                const product = edge?.node?.product;
                const productId = product?.id;
                const price = Number(product?.price) || 0;
                const multiplicity = Number(product?.quantityMultiplicity) || 1;
                const quantity = Number(edge?.node?.quantity) || 0;

                if (!productId || multiplicity <= 1 || product?.stockAvailabilityStatus === 'OUT_OF_STOCK') {
                    continue;
                }

                const normalized = Math.ceil(quantity / multiplicity) * multiplicity;

                if (normalized !== quantity) {
                    try {
                        await apolloClient.mutate({
                            mutation: UPDATE_CART_ITEM_MUTATION,
                            variables: {
                                productId,
                                quantity: normalized
                            }
                        });

                        // Обновляем через мутацию для immutability (хотя edge.node все еще мутируется, но это ограничение GraphQL структуры)
                        // Мутация edge.node необходима для синхронизации с GraphQL структурой
                        // Это ограничение работы с GraphQL данными, но мы обновляем только локально
                        edge.node.quantity = normalized;
                        edge.node.product.price = price;
                    } catch (error) {
                        logError('Failed to normalize cart item quantity', error);
                    }
                }
            }
        },

        recalculateCartState() {
            const newCart = {};
            let totalQuantity = 0;
            let totalPrice = 0;

            if (Array.isArray(this.cartItems)) {
                for (const edge of this.cartItems) {
                    const product = edge?.node?.product;
                    const productId = product?.id;
                    const quantity = Number(edge?.node?.quantity) || 0;
                    const price = Number(product?.price) || 0;

                    if (productId && quantity > 0) {
                        newCart[productId] = quantity;
                        totalQuantity += quantity;
                        totalPrice += price * quantity;
                    }
                }
            }

            this.cart = newCart;
            this.cartCount = totalQuantity;
            this.cartPrice = totalPrice;
        },

        removeUnavailableFromTotals() {
            if (!Array.isArray(this.cartItems)) {
                return;
            }

            let quantity = 0;
            let price = 0;

            for (const edge of this.cartItems) {
                const product = edge?.node?.product;
                if (!product || product.stockAvailabilityStatus === 'OUT_OF_STOCK') {
                    continue;
                }
                const itemQuantity = Number(edge?.node?.quantity) || 0;
                const itemPrice = Number(product.price) || 0;

                quantity += itemQuantity;
                price += itemQuantity * itemPrice;
            }

            this.cartCount = quantity;
            this.cartPrice = price;
        }
    }
})